'use client'

import { Fragment } from 'react'
import { Box, Center, Group, LoadingOverlay, Pagination, ScrollArea, Select, Table, Text } from '@mantine/core'
import { faArrowDown, faArrowUp } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { flexRender, getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table'
import styles from './styles.module.css'
import type { Column, RowData } from '@tanstack/react-table'
import type { CSSProperties } from 'react'
import type { MantineDataTableProps } from './types'

/** Class names cells can apply to adapt to the table's density. */
export const dataTableClassNames = {
  /** Hidden when `density="compact"`, e.g. the email line under a name. */
  hideWhenCompact: styles.hideWhenCompact,
}

export function MantineDataTable<TData extends RowData>({
  // Mantine-specific props
  mantineTableProps,
  height,
  minHeight = 200,
  stickyHeader = true,
  loading = false,
  emptyState,
  noRecordsText = 'No records',
  onRowClick,
  getRowHref,
  renderSubComponent,
  showPagination = true,
  showRecordCount = false,
  hideTotal = false,
  showPageSizeSelector = false,
  pageSizeOptions = [10, 20, 50, 100],
  density = 'comfortable',
  className,
  // TanStack Table options (pass-through)
  data,
  columns,
  state,
  ...tableOptions
}: MantineDataTableProps<TData>) {
  // Create the table instance with all TanStack options
  const table = useReactTable({
    data,
    columns,
    state,
    getCoreRowModel: getCoreRowModel(),
    // Enable row expansion for `renderSubComponent`; overridable via tableOptions.
    // Harmless when no rows are ever expanded.
    getExpandedRowModel: getExpandedRowModel(),
    ...tableOptions,
  })

  // Get pagination state for UI
  const pagination = state?.pagination
  const totalRows = tableOptions.rowCount ?? 0
  const pageCount = tableOptions.pageCount ?? (totalRows && pagination ? Math.ceil(totalRows / pagination.pageSize) : 0)

  // Calculate record range for display (e.g., "1-20 of 204")
  const recordStart = pagination ? pagination.pageIndex * pagination.pageSize + 1 : 1
  const recordEnd = pagination ? Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalRows) : totalRows

  // Get all header groups and rows
  const headerGroups = table.getHeaderGroups()
  const rows = table.getRowModel().rows
  const hasData = rows.length > 0

  // Get common pinning styles using TanStack Table's built-in methods
  // Based on: https://tanstack.com/table/latest/docs/framework/react/examples/column-pinning-sticky
  const getCommonPinningStyles = (column: Column<TData, unknown>): CSSProperties => {
    const isPinned = column.getIsPinned()
    const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left')
    const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right')

    // All pinned columns need the same z-index so scrolling content goes under all of them
    const zIndex = isPinned ? 1 : 0

    return {
      boxShadow: isLastLeftPinnedColumn
        ? '-5px 0 5px -5px rgba(0, 0, 0, 0.15) inset'
        : isFirstRightPinnedColumn
          ? '5px 0 5px -5px rgba(0, 0, 0, 0.15) inset'
          : undefined,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      position: isPinned ? 'sticky' : 'relative',
      zIndex,
      backgroundColor: isPinned ? 'var(--mantine-color-body)' : undefined,
    }
  }

  // Extract style props with defaults
  const {
    striped = false,
    highlightOnHover = false,
    withTableBorder = false,
    withColumnBorders = false,
    withRowBorders = true,
    borderRadius,
    horizontalSpacing = 'md',
    // Compact halves the row padding. An explicit verticalSpacing still wins.
    verticalSpacing = density === 'compact' ? 'xs' : 'sm',
    layout = 'auto',
  } = mantineTableProps ?? {}

  // Handle page change for pagination
  const handlePageChange = (page: number) => {
    if (tableOptions.onPaginationChange && pagination) {
      tableOptions.onPaginationChange({
        pageIndex: page - 1, // Mantine uses 1-based, TanStack uses 0-based
        pageSize: pagination.pageSize,
      })
    }
  }

  // Handle page size change
  const handlePageSizeChange = (value: string | null) => {
    if (tableOptions.onPaginationChange && pagination && value) {
      tableOptions.onPaginationChange({
        pageIndex: 0, // Reset to first page when changing page size
        pageSize: parseInt(value, 10),
      })
    }
  }

  // Convert page size options to Select data format
  const pageSizeSelectData = pageSizeOptions.map((size) => ({
    value: size.toString(),
    label: `${size} / page`,
  }))

  return (
    // data-density lets cells hide secondary content via `dataTableClassNames.hideWhenCompact`.
    <Box className={`${styles.root} ${className ?? ''}`.trim()} data-density={density}>
      <Box
        pos="relative"
        style={{
          borderRadius: borderRadius ? `var(--mantine-radius-${borderRadius})` : undefined,
          border: withTableBorder ? '1px solid var(--mantine-color-gray-3)' : undefined,
          overflow: 'hidden',
        }}
      >
        <LoadingOverlay visible={loading} zIndex={10} overlayProps={{ blur: 2 }} />

        <ScrollArea.Autosize mah={height === 'auto' ? undefined : height} mih={minHeight} type="auto">
          <Table
            striped={striped}
            highlightOnHover={highlightOnHover}
            withColumnBorders={withColumnBorders}
            withRowBorders={withRowBorders}
            horizontalSpacing={horizontalSpacing}
            verticalSpacing={verticalSpacing}
            layout={layout}
            className={styles.table}
            stickyHeader={stickyHeader}
            stickyHeaderOffset={0}
          >
            <Table.Thead>
              {headerGroups.map((headerGroup) => (
                <Table.Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSortable = header.column.getCanSort()
                    const sortDirection = header.column.getIsSorted()
                    const { column } = header
                    const pinningStyles = getCommonPinningStyles(column)
                    const isPinned = column.getIsPinned()

                    // Mantine v9 no longer draws the sticky header's bottom border as a
                    // border-bottom on the row: border-collapse drops borders on sticky
                    // cells, so it suppresses the row border and repaints the line as an
                    // inset box-shadow on each th. The pinned drop shadow above is an
                    // inline style, which beats that rule — so on the edge-pinned columns
                    // it has to carry the line too, or the header shows a gap exactly as
                    // wide as those columns. Only body cells keep a real border-bottom.
                    const headerBoxShadow =
                      pinningStyles.boxShadow && withRowBorders
                        ? `${pinningStyles.boxShadow}, inset 0 -1px 0 var(--table-border-color)`
                        : pinningStyles.boxShadow

                    return (
                      <Table.Th
                        key={header.id}
                        style={{
                          width: column.getSize() !== 150 ? column.getSize() : undefined,
                          minWidth: column.getSize() !== 150 ? column.getSize() : undefined,
                          maxWidth: column.getSize() !== 150 ? column.getSize() : undefined,
                          cursor: isSortable ? 'pointer' : undefined,
                          userSelect: isSortable ? 'none' : undefined,
                          whiteSpace: 'nowrap',
                          ...pinningStyles,
                          boxShadow: headerBoxShadow,
                          // Header needs higher z-index for sticky header + sticky column overlap
                          zIndex: isPinned ? 3 : undefined,
                        }}
                        onClick={isSortable ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <Group gap={6} wrap="nowrap">
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {sortDirection && (
                            <FontAwesomeIcon
                              icon={sortDirection === 'asc' ? faArrowUp : faArrowDown}
                              size="sm"
                              color="var(--mantine-color-blue-6)"
                            />
                          )}
                        </Group>
                      </Table.Th>
                    )
                  })}
                </Table.Tr>
              ))}
            </Table.Thead>

            <Table.Tbody>
              {hasData ? (
                rows.map((row) => {
                  const rowHref = getRowHref?.(row)
                  const isInteractive = !!(onRowClick || rowHref)

                  // Handle row click - support cmd/ctrl+click to open in new tab
                  const handleRowClick = (e: React.MouseEvent) => {
                    if (rowHref && (e.metaKey || e.ctrlKey)) {
                      // Cmd/Ctrl+click: open in new tab
                      window.open(rowHref, '_blank')
                    } else if (onRowClick) {
                      onRowClick(row)
                    }
                  }

                  // Handle keyboard activation (Enter or Space)
                  const handleKeyDown = (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      if (onRowClick) {
                        onRowClick(row)
                      } else if (rowHref) {
                        window.location.href = rowHref
                      }
                    }
                  }

                  return (
                    <Fragment key={row.id}>
                      <Table.Tr
                        onClick={isInteractive ? handleRowClick : undefined}
                        onKeyDown={isInteractive ? handleKeyDown : undefined}
                        tabIndex={isInteractive ? 0 : undefined}
                        role={isInteractive ? (rowHref ? 'link' : 'button') : undefined}
                        style={{ cursor: isInteractive ? 'pointer' : undefined }}
                        data-selected={row.getIsSelected() || undefined}
                        className={
                          `${row.getIsSelected() ? styles.selectedRow : ''} ${isInteractive ? styles.interactiveRow : ''}`.trim() ||
                          undefined
                        }
                      >
                        {row.getVisibleCells().map((cell) => {
                          const { column } = cell
                          const isPinned = column.getIsPinned()
                          const pinningStyles = getCommonPinningStyles(column)
                          const columnSize = column.getSize()

                          // Override background for selected rows on pinned columns
                          const cellStyles: CSSProperties = {
                            ...pinningStyles,
                            ...(isPinned && row.getIsSelected()
                              ? { backgroundColor: 'var(--mantine-color-blue-light)' }
                              : {}),
                            // Apply width constraints to cell
                            ...(columnSize !== 150
                              ? {
                                  maxWidth: columnSize,
                                  width: columnSize,
                                  minWidth: columnSize,
                                  // A sized column can't grow, so content that can't wrap — a long
                                  // unbreakable id like LEAD302026080045, or a cell that sets
                                  // white-space: nowrap — spills past the padding box and paints
                                  // over the next column. Clip it at the cell edge instead.
                                  // Deliberately no white-space here: it inherits, and cells that
                                  // stack content (name over email) must keep wrapping.
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }
                              : {}),
                          }

                          return (
                            <Table.Td key={cell.id} style={cellStyles}>
                              {flexRender(column.columnDef.cell, cell.getContext())}
                            </Table.Td>
                          )
                        })}
                      </Table.Tr>
                      {renderSubComponent && row.getIsExpanded() && (
                        <Table.Tr>
                          <Table.Td colSpan={row.getVisibleCells().length} p={0}>
                            {renderSubComponent(row)}
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Fragment>
                  )
                })
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={headerGroups[0]?.headers.length ?? 1}>
                    <Center py="xl">{emptyState ?? <Text c="dimmed">{noRecordsText}</Text>}</Center>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
      </Box>

      {(showPagination || showRecordCount || showPageSizeSelector) &&
        pagination &&
        (pageCount > 1 || showRecordCount || showPageSizeSelector) && (
          <Group justify="space-between" align="center" mt="md">
            {/* Left side: Record count - hidden when no results */}
            <Group gap="md">
              {showRecordCount && totalRows > 0 && (
                <Text size="sm" c="dimmed">
                  {recordStart}-{recordEnd}
                  {!hideTotal && ` of ${totalRows.toLocaleString()}`}
                </Text>
              )}
            </Group>

            {/* Center: Pagination */}
            {showPagination && pageCount > 1 && (
              <Pagination total={pageCount} value={pagination.pageIndex + 1} onChange={handlePageChange} />
            )}

            {/* Right side: Page size selector */}
            <Group gap="md">
              {showPageSizeSelector && (
                <Select
                  size="xs"
                  w={110}
                  value={pagination.pageSize.toString()}
                  onChange={handlePageSizeChange}
                  data={pageSizeSelectData}
                  allowDeselect={false}
                />
              )}
            </Group>
          </Group>
        )}
    </Box>
  )
}
